import { z } from "zod/v4";

import { AuditScreenshotSchema } from "../../../functions/opportunity-audit/records/screenshot";

import { ReadResult, readVersioned, Upcasters } from "./versioned-payload";

import { AuditContentSpecSchema } from "./audit-content-spec";
import { AuditRecommendationOutcomeSchema, decided } from "./audit-decided";
import {
  AuditCheckoutBlockValueSchema,
  AuditCheckoutContentValueSchema,
  AuditContentAlternativeValueSchema,
  AuditContentChangeValueSchema,
  AuditContentLocationValueSchema,
  AuditContentPageValueSchema,
  AuditKeyMetricValueSchema,
  AuditOfferProductsValueSchema,
  AuditOfferTermsValueSchema,
  AuditOfferTypeValueSchema,
  AuditPricingPricesValueSchema,
  AuditPricingProductsValueSchema,
  AuditShippingChangeValueSchema,
  AuditShippingValueValueSchema,
  AuditShippingZoneValueSchema,
  AuditTestModeValueSchema,
} from "./audit-recommendation-values";
import { AuditRecommendationPresentationSchema } from "./audit-recommendation-presentation";
import {
  AuditPostPurchaseChangeValueSchema,
  AuditPostPurchaseLayoutValueSchema,
  AuditPostPurchaseProductsValueSchema,
  AuditPostPurchaseTermsValueSchema,
} from "./audit-post-purchase";

/**
 * One recommendation, typed by the kind of test it is.
 *
 * **A card has a named slot for every decision its test type is made of.** The
 * tree that composes a type is authored in `recommend.contract.ts`, and these
 * mirror it: pricing is products then prices, shipping is zone then change then
 * value, and every type ends with mode and key metric. A field name is the
 * decision's own label in the merchant's words, from `NODE_LABEL`.
 *
 * This replaces a `decisions[]` array whose entries were `{ node: string,
 * answers: { value: unknown }[] }`. Two things were wrong with it and both were
 * costing us. A consumer wanting one decision had to search the array by node
 * name and then narrow `unknown` at runtime — `present-recommendation.tool.ts`
 * does exactly that for `mode`, with a string compare, and it is the only field
 * anything bothered to dig out. And a decision that did not happen was simply
 * absent from the array, so a node that failed reached the merchant as one
 * fewer row with no reason given. A named slot holding `Decided` says which of
 * the three things happened and why, in every case.
 */

/** How big the step is that removes a blocked limitation. */
export const AuditUnlockCostList = [
  "setting",
  "reauthorisation",
  "waiting",
  "development",
  "developmentByTheirDeveloper",
  "money",
] as const;

/** Every step the audit knows a merchant can take so a test runs, or runs better. */
export const AuditUnlockTypeList = [
  "permissionDenied",
  "storefrontPassword",
  "shippingRatesOff",
  "deliveryZonesMissing",
  "shippingTestRunning",
  "shippingTestNotEnded",
  "planExcludesType",
  "shopifyPlusRequired",
  "planProductLimit",
  "productUnofferable",
  "productCostsMissing",
  "postPurchaseSameAudienceLive",
  "postPurchaseSwitchedOff",
  "cartMetafieldsOff",
  "writeOrdersMissing",
  "storefrontTokenMissing",
  "postPurchaseAppNotSelected",
  "offerLimitReached",
  "checkoutBlockPlacement",
] as const;

export type AuditUnlockType = (typeof AuditUnlockTypeList)[number];

/**
 * Something the merchant can do so a test runs, or runs better: one step, how
 * big it is, and why. Taken from the limitation record, never written by the
 * model, so it reads the same way every time (Guido, 2026-09-14).
 */
export const AuditUnlockSchema = z.object({
  /** Which step it is. Optional because cards shown before it have none. */
  type: z.enum(AuditUnlockTypeList).optional(),
  /** What the merchant does, in their words: "Update the app's Shopify permissions". */
  action: z.string(),
  cost: z.enum(AuditUnlockCostList),
  /** What it removes. */
  reason: z.string(),
});

/**
 * How we know something, which governs how it may be worded: only `measured`
 * may read as a fact about this store.
 */
export const AuditHowSchema = z.enum([
  "measured",
  "population",
  "authored",
  "judged",
  "stated",
]);

/**
 * What the walk knows about a recommendation beyond its decisions: how long it
 * takes, why it was chosen, what it rests on and what weakens it. Read back
 * from the walk, never written by the model.
 */
export const AuditRecommendationContextSchema = z.object({
  length: z.object({
    /** Null when the orders could not be counted or none reach the test. */
    days: z.number().nullable(),
    ordersPerDay: z.number().nullable(),
    arms: z.number(),
    /** Longer than the days we give a test to reach a decision. */
    slow: z.boolean(),
    /** The sentence, with the orders it is sized on. */
    text: z.string(),
  }),
  /**
   * Why it was picked, in a line, from its strongest reasons; null when nothing
   * spoke for it. Optional because cards shown before it have none.
   */
  why: z.string().nullable().optional(),
  /** Why the ranking chose it, strongest first. A negative lean counted against it. */
  reasons: z.array(
    z.object({
      entity: z.string(),
      lean: z.number(),
      because: z.string(),
      /** What the lean rests on, as a stable name, where the entity gives one. */
      key: z.string().optional(),
    })
  ),
  /** What the audit read about the store that this test rests on. */
  facts: z.array(z.string()),
  /** What makes it work less well. */
  cautions: z.array(z.string()),
  /** What it assumes about questions still open. */
  assumes: z.array(z.string()),
});

/**
 * On every card, whatever its type.
 *
 * `outcome` is the branch's own record. A node that failed or rejected says so
 * in its own slot; a walk that gave up on the path has no such slot to use.
 */
const everyCard = {
  /** The composed title and description, for what lists the card in one line. */
  title: z.string().describe("The test in one line"),
  summary: z
    .string()
    .describe("What running this test would tell the merchant"),
  outcome: AuditRecommendationOutcomeSchema,
  /** One per step, from the blocked limitations of the options the card was built from. */
  unlocks: z.array(AuditUnlockSchema).default([]),
  /**
   * The card's lasting id, a UUID made once per candidate in a walk. It is the
   * id of its stored recommendation, and an experience built from the card
   * points back to it. Optional because cards shown before it have none.
   */
  recommendationId: z.string().optional(),
  /** The walk run it came from, whose trace is stored under that name. */
  walkRun: z.string().optional(),
  /** Its place in the set it was shown in, from 1. Optional because cards shown before it have none. */
  rank: z.number().int().positive().optional(),
  /** Optional because cards shown before it have none. */
  context: AuditRecommendationContextSchema.optional(),
  /** How the merchant reads it. Optional because cards shown before it have none. */
  presentation: AuditRecommendationPresentationSchema.optional(),
  /** The shape the card was written in. Absent means 0, before versions existed. */
  schemaVersion: z.number().int().min(0).optional(),
};

/** Settled for every type, after the type's own decisions. */
const sharedTail = {
  mode: decided(AuditTestModeValueSchema),
  keyMetric: decided(AuditKeyMetricValueSchema),
};

export const AuditPricingRecommendationSchema = z.object({
  type: z.literal("pricing"),
  ...everyCard,
  products: decided(AuditPricingProductsValueSchema),
  prices: decided(AuditPricingPricesValueSchema),
  ...sharedTail,
});

export const AuditShippingRecommendationSchema = z.object({
  type: z.literal("shipping"),
  ...everyCard,
  zone: decided(AuditShippingZoneValueSchema),
  change: decided(AuditShippingChangeValueSchema),
  newValue: decided(AuditShippingValueValueSchema),
  ...sharedTail,
});

export const AuditOfferRecommendationSchema = z.object({
  type: z.literal("offer"),
  ...everyCard,
  offer: decided(AuditOfferTypeValueSchema),
  products: decided(AuditOfferProductsValueSchema),
  terms: decided(AuditOfferTermsValueSchema),
  ...sharedTail,
});

export const AuditContentRecommendationSchema = z.object({
  type: z.literal("content"),
  ...everyCard,
  page: decided(AuditContentPageValueSchema),
  location: decided(AuditContentLocationValueSchema),
  /**
   * A list, because most content tests change more than one thing: 42.3% of
   * 14,828 classified content tests change one. Today's generator puts one in.
   */
  changes: decided(z.array(AuditContentChangeValueSchema).min(1)),
  testedAgainst: decided(AuditContentAlternativeValueSchema),
  /** What to build, by elements of one page read. Cards composed before it have none. */
  spec: AuditContentSpecSchema.optional(),
  /** Whether a screenshot is coming, set at the pick. Optional because cards shown before it have none. */
  screenshot: AuditScreenshotSchema.optional(),
  ...sharedTail,
});

export const AuditCheckoutRecommendationSchema = z.object({
  type: z.literal("checkout"),
  ...everyCard,
  block: decided(AuditCheckoutBlockValueSchema),
  content: decided(AuditCheckoutContentValueSchema),
  ...sharedTail,
});

/**
 * A post-purchase test is a set of arms, not a path: products and terms hold
 * one entry per distinct offer, keyed to the arms that show it. An arm with
 * no offer has neither.
 */
export const AuditPostPurchaseRecommendationSchema = z.object({
  type: z.literal("postPurchase"),
  ...everyCard,
  change: decided(AuditPostPurchaseChangeValueSchema),
  products: decided(z.array(AuditPostPurchaseProductsValueSchema).min(1)),
  terms: decided(z.array(AuditPostPurchaseTermsValueSchema).min(1)),
  layout: decided(AuditPostPurchaseLayoutValueSchema),
  /** Who the test is shown to. Every buyer until a merchant can name an audience. */
  audience: z.literal("wholeStore"),
  ...sharedTail,
});

export const AuditRecommendationSchema = z.discriminatedUnion("type", [
  AuditPricingRecommendationSchema,
  AuditShippingRecommendationSchema,
  AuditOfferRecommendationSchema,
  AuditContentRecommendationSchema,
  AuditCheckoutRecommendationSchema,
  AuditPostPurchaseRecommendationSchema,
]);

export type AuditHow = z.infer<typeof AuditHowSchema>;
export type AuditPricingRecommendation = z.infer<
  typeof AuditPricingRecommendationSchema
>;
export type AuditShippingRecommendation = z.infer<
  typeof AuditShippingRecommendationSchema
>;
export type AuditOfferRecommendation = z.infer<
  typeof AuditOfferRecommendationSchema
>;
export type AuditContentRecommendation = z.infer<
  typeof AuditContentRecommendationSchema
>;
export type AuditCheckoutRecommendation = z.infer<
  typeof AuditCheckoutRecommendationSchema
>;
export type AuditPostPurchaseRecommendation = z.infer<
  typeof AuditPostPurchaseRecommendationSchema
>;
export type AuditUnlock = z.infer<typeof AuditUnlockSchema>;
export type AuditRecommendationContext = z.infer<
  typeof AuditRecommendationContextSchema
>;
export type AuditUnlockCost = (typeof AuditUnlockCostList)[number];
export type AuditRecommendation = z.infer<typeof AuditRecommendationSchema>;

/**
 * The card's shape version. A new field is optional unless an upcaster can
 * fill it from the body, so most changes keep the version. A field the build
 * needs that no upcaster can fill stays optional, and the builder reads it
 * from Shopify when it is missing. Any other change moves the version, with an
 * upcaster when stored cards would stop reading.
 *
 * 2 — 2026-10-14. Unlocks gain `checkoutBlockPlacement`. Version 1 cards read
 * unchanged.
 *
 * 3 — 2026-10-17. An element's description drops `shownMatches`, and `copies`
 * counts hidden copies too. Version 2 cards read unchanged.
 *
 * 4 — 2026-10-19. A content location's theme name moves to `themeName`, and
 * its `sectionType` is the section reading's type. The location's own schema
 * reads version 3 locations, so no upcaster. A line's `claims` is optional and
 * no longer written.
 *
 * 5 — 2026-10-20. A content card's spec can be `described`: the place and the
 * change in words, which the builder places itself. `exact` and `missing` are
 * no longer written. Version 4 cards read unchanged.
 */
export const AUDIT_RECOMMENDATION_VERSION = 5;

/** Keyed by the version each upgrades from. Version 0 reads as it is today. */
export const AUDIT_RECOMMENDATION_UPCASTERS: Upcasters = {};

/** A stored card, read today, stamped with the current version. */
export function readRecommendation(
  raw: unknown
): ReadResult<AuditRecommendation> {
  const version =
    raw && typeof raw === "object" && "schemaVersion" in raw
      ? Number((raw as { schemaVersion?: unknown }).schemaVersion)
      : undefined;
  const read = readVersioned({
    raw,
    version: Number.isFinite(version) ? version : undefined,
    current: AUDIT_RECOMMENDATION_VERSION,
    upcasters: AUDIT_RECOMMENDATION_UPCASTERS,
    schema: AuditRecommendationSchema,
  });
  return read.type === "read"
    ? {
        type: "read",
        value: {
          ...read.value,
          schemaVersion: AUDIT_RECOMMENDATION_VERSION,
        },
      }
    : read;
}
